'use client';

import React from 'react';
import Link from 'next/link';
import Header from '@/components/common/Header';
import Footer from '@/app/homepage/components/SiteFooter';
import { ProtectedAppImage } from '@/components/ProtectedImage/ProtectedAppImage';
import { useAuth } from '@/contexts/AuthContext';
import { getAuthRedirectUrl } from '@/lib/redirectUtils';
import { SeoService, SERVICES } from '@/lib/seo/services';
import Breadcrumbs from './Breadcrumbs';
import BeforeAfterSlider from './BeforeAfterSlider';
import ServiceFAQ from './ServiceFAQ';

const Check = ({ className = 'w-5 h-5' }: { className?: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
      clipRule="evenodd"
    />
  </svg>
);

const dotGrid = `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`;

export default function ServiceLandingClient({ service }: { service: SeoService }) {
  const { isAuthenticated } = useAuth();
  const signupUrl = getAuthRedirectUrl(isAuthenticated);
  const related = service.related.map((slug) => SERVICES[slug]).filter(Boolean);
  const hero = service.beforeAfter[0];

  return (
    <div className="bg-[#0B0D17] text-white">
      <Header />

      {/* ===================== HERO ===================== */}
      <section className="relative overflow-hidden border-b border-white/[0.06]">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-[1000px] h-[1000px] bg-purple-600/10 rounded-full blur-[160px] animate-glow-breathe" />
          <div className="absolute bottom-1/4 right-1/4 w-[800px] h-[800px] bg-blue-600/5 rounded-full blur-[140px] animate-glow-breathe-delayed" />
          <div className="absolute inset-0 opacity-[0.025]" style={{ backgroundImage: dotGrid }} />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 pt-6 pb-16 lg:pb-24">
          <Breadcrumbs
            items={[
              { name: 'Home', href: '/' },
              { name: 'Services', href: '/services' },
              { name: service.name },
            ]}
          />

          <div className="grid lg:grid-cols-2 gap-12 items-center mt-8">
            <div className="space-y-7">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.12] text-purple-200 text-xs font-medium">
                <span>Starting at {service.priceFrom} {service.priceUnit}</span>
              </div>

              <h1 className="text-4xl lg:text-6xl font-bold leading-[1.05] tracking-tight">{service.h1}.</h1>
              <p className="text-lg lg:text-xl text-gray-400 leading-relaxed">{service.tagline}</p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link href={signupUrl}>
                  <button className="group w-full sm:w-auto px-9 py-4 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white rounded-xl font-bold text-lg transition-all duration-300 flex items-center justify-center gap-2 shadow-xl shadow-purple-500/30 hover:-translate-y-0.5">
                    Place an Order
                    <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </button>
                </Link>
                <Link href="#gallery">
                  <button className="w-full sm:w-auto px-9 py-4 bg-white/[0.03] hover:bg-white/[0.08] border border-white/[0.15] hover:border-white/30 text-white rounded-xl font-bold text-lg transition-all duration-300">
                    See the results
                  </button>
                </Link>
              </div>

              <div className="grid grid-cols-3 gap-4 pt-2">
                <Stat label="From" value={service.priceFrom} />
                <Stat label="Turnaround" value={service.turnaround} />
                <Stat label="Revisions" value="Unlimited" />
              </div>
            </div>

            {/* Hero before/after slider — proof up front */}
            <div className="relative">
              <div className="absolute -inset-4 bg-purple-600/15 rounded-3xl blur-[80px] -z-10" />
              <BeforeAfterSlider
                before={hero.before}
                after={hero.after}
                alt={`${service.name} — ${hero.label}`}
                className="h-[340px] lg:h-[460px] shadow-2xl"
              />
              <p className="text-center text-xs text-gray-500 mt-3">Drag the slider to compare before &amp; after</p>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== WHAT IT IS ===================== */}
      <section className="relative py-16 lg:py-24 px-6 lg:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl lg:text-4xl font-bold tracking-tight mb-5">What is {service.name.toLowerCase()}?</h2>
          <p className="text-lg text-gray-400 leading-relaxed mb-6">{service.intro}</p>
          <p className="text-gray-400 leading-relaxed">{service.whyItMatters}</p>
        </div>

        <div className="max-w-7xl mx-auto grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-14">
          {service.benefits.map((b, i) => (
            <div key={i} className="group bg-[#13151F]/80 border border-white/[0.08] rounded-2xl p-6 hover:border-purple-500/30 hover:-translate-y-1 transition-all duration-300">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500/20 to-blue-500/10 border border-purple-500/20 flex items-center justify-center text-2xl mb-4 group-hover:scale-110 transition-transform">
                {b.icon}
              </div>
              <h3 className="text-base font-bold text-white mb-2">{b.title}</h3>
              <p className="text-sm text-gray-400 leading-relaxed">{b.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ===================== GALLERY ===================== */}
      <section id="gallery" className="relative py-16 lg:py-24 px-6 lg:px-12 bg-white/[0.015] border-y border-white/[0.06] scroll-mt-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 max-w-2xl mx-auto">
            <h2 className="text-3xl lg:text-4xl font-bold tracking-tight mb-4">See the transformation</h2>
            <p className="text-lg text-gray-400 leading-relaxed">Real before and after examples. Drag any slider to compare.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {service.beforeAfter.map((pair, i) => (
              <div key={i}>
                <BeforeAfterSlider before={pair.before} after={pair.after} alt={`${service.name} — ${pair.label}`} className="h-64" />
                <p className="text-sm text-gray-400 mt-3 font-medium">{pair.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== FORMATS + PRICING ===================== */}
      <section className="relative py-16 lg:py-24 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-10 items-start">
          <div className="bg-[#13151F]/80 border border-white/[0.08] rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-white mb-5">What you get</h2>
            <ul className="space-y-3 mb-8">
              {[
                `${service.turnaround} standard turnaround`,
                'Unlimited free revisions',
                'Pay only after you approve',
                'Expert human editors — no automation',
                'Secure file handling & privacy',
              ].map((f) => (
                <li key={f} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-300">{f}</span>
                </li>
              ))}
            </ul>
            <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wide mb-3">Supported file formats</h3>
            <div className="flex flex-wrap gap-2">
              {service.formats.map((fmt) => (
                <span key={fmt} className="text-xs font-medium text-gray-300 bg-white/[0.04] border border-white/[0.08] rounded-full px-3 py-1.5">
                  {fmt}
                </span>
              ))}
            </div>
          </div>

          <div className="relative bg-gradient-to-b from-purple-600/20 to-[#13151F]/90 border-2 border-purple-500/40 rounded-2xl p-8 shadow-[0_30px_80px_rgba(124,58,237,0.2)]">
            <h2 className="text-2xl font-bold text-white mb-2">Transparent pricing</h2>
            <p className="text-gray-300 text-sm mb-6">No subscriptions. No minimums. Pay per image.</p>
            <div className="flex items-baseline gap-2 mb-6">
              <span className="text-6xl font-bold text-white">{service.priceFrom}</span>
              <span className="text-gray-300">{service.priceUnit}</span>
            </div>
            <ul className="space-y-3 mb-8">
              {['Volume discounts on larger orders', 'Rush delivery available', 'Final price never exceeds the estimate'].map((f) => (
                <li key={f} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-purple-300 flex-shrink-0 mt-0.5" />
                  <span className="text-white">{f}</span>
                </li>
              ))}
            </ul>
            <Link href={signupUrl}>
              <button className="w-full py-4 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white rounded-xl font-bold text-lg transition-all shadow-lg shadow-purple-500/30 hover:-translate-y-0.5">
                Get Started
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* ===================== FAQ ===================== */}
      <section className="relative py-16 lg:py-24 px-6 lg:px-12 bg-white/[0.015] border-y border-white/[0.06]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 max-w-2xl mx-auto">
            <h2 className="text-3xl lg:text-4xl font-bold tracking-tight mb-4">Frequently asked questions</h2>
            <p className="text-lg text-gray-400">Everything you need to know about {service.name.toLowerCase()}.</p>
          </div>
          <ServiceFAQ faqs={service.faqs} />
        </div>
      </section>

      {/* ===================== RELATED SERVICES (internal links) ===================== */}
      {related.length > 0 && (
        <section className="relative py-16 lg:py-24 px-6 lg:px-12">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl lg:text-3xl font-bold tracking-tight mb-8 text-center">Explore related services</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  href={`/services/${r.slug}`}
                  className="group bg-[#13151F]/80 border border-white/[0.08] rounded-2xl p-6 hover:border-purple-500/30 hover:-translate-y-1 transition-all duration-300"
                >
                  <h3 className="text-lg font-bold text-white mb-1.5 group-hover:text-purple-300 transition-colors">{r.name}</h3>
                  <p className="text-sm text-gray-400 leading-relaxed line-clamp-2 mb-4">{r.tagline}</p>
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-purple-400 group-hover:gap-2.5 transition-all">
                    Learn more
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===================== CTA ===================== */}
      <section className="relative py-20 lg:py-28 px-6 lg:px-12 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] bg-purple-600/10 rounded-full blur-[160px] animate-glow-breathe" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <div className="relative bg-[#13151F]/95 rounded-3xl border-2 border-white/[0.12] shadow-[0_30px_80px_rgba(0,0,0,0.6)] p-12 lg:p-16">
            <div className="absolute -inset-4 bg-purple-600/15 rounded-3xl blur-[80px] -z-10" />
            <h2 className="text-3xl lg:text-5xl font-bold tracking-tight mb-5">Ready to get started?</h2>
            <p className="text-lg text-gray-400 leading-relaxed mb-10 max-w-2xl mx-auto">
              Create your free account and place your first {service.name.toLowerCase()} order in minutes. You only pay once you approve the edits.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href={signupUrl}>
                <button className="px-10 py-4 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white font-bold text-lg rounded-xl transition-all duration-300 shadow-xl shadow-purple-500/30 hover:-translate-y-0.5">
                  Start Free Account
                </button>
              </Link>
              <Link href="/contact">
                <button className="px-10 py-4 bg-white/[0.03] hover:bg-white/[0.08] text-white font-bold text-lg rounded-xl transition-all duration-300 border border-white/[0.15] hover:border-white/30">
                  Contact Sales
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      <style jsx global>{`
        @keyframes glow-breathe {
          0%, 100% { opacity: 0.1; transform: scale(1); }
          50% { opacity: 0.15; transform: scale(1.05); }
        }
        @keyframes glow-breathe-delayed {
          0%, 100% { opacity: 0.05; transform: scale(1); }
          50% { opacity: 0.08; transform: scale(1.03); }
        }
        .animate-glow-breathe { animation: glow-breathe 8s ease-in-out infinite; }
        .animate-glow-breathe-delayed { animation: glow-breathe-delayed 10s ease-in-out infinite; animation-delay: 2s; }
      `}</style>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-4 text-center">
      <div className="text-xl lg:text-2xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">{value}</div>
      <div className="text-xs text-gray-500 mt-1">{label}</div>
    </div>
  );
}
